'use client';
import type { ElementType } from 'react';
import { Box, Link, Text } from '@skin-seoul/design-system';
import { useLoyaltyService, useUser, parseMessageWithBold, type Country, type Currency } from '@skin-seoul/service';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import { getAuthenticatedMilestoneBannerMessage } from '../constants/milestoneBanner';
import { getSignInBannerSuffix } from '../constants/signInBanner';
import { LoyaltyPointsBannerSkeleton } from './LoyaltyPointsBannerSkeleton';

type LoyaltyPointsBannerContainerProps = {
  linkComponent?: ElementType;
  cartTotalPrice: number;
  country: Country;
  currency: Currency;
  isFirstOrder: boolean;
};

export const LoyaltyPointsBannerContainer = ({
  linkComponent,
  cartTotalPrice,
  country,
  currency,
  isFirstOrder,
}: LoyaltyPointsBannerContainerProps) => {
  const LinkComponent = linkComponent || Link;
  const { onCartClose } = useCartDrawer();
  const { user, isAuthenticated, isLoading: isUserLoading } = useUser();
  const { useGetCustomerBalance } = useLoyaltyService();

  const { data: balanceData, isLoading: isBalanceLoading } = useGetCustomerBalance({
    customerId: String(user?.userId ?? ''),
    enabled: isAuthenticated && Boolean(user?.userId),
  });

  // Show skeleton while loading user or balance data
  if (isUserLoading || isBalanceLoading) {
    return <LoyaltyPointsBannerSkeleton />;
  }

  // Show sign-in banner if not authenticated or no data
  if (!isAuthenticated || !user?.userId || !balanceData) {
    const suffix = getSignInBannerSuffix(cartTotalPrice, country);
    return (
      <Box
        width="100%"
        backgroundColor="primary.75"
        padding="4px"
        textAlign="center"
        borderBottomLeftRadius="12px"
        borderBottomRightRadius="12px"
      >
        <Text
          as="span"
          textStyle="label.medium"
          fontWeight="medium"
          color="primary.500"
        >
          <LinkComponent
            unstyled
            href="/login"
            textDecoration="underline"
            fontWeight="semibold"
            onClick={onCartClose}
          >
            Sign in
          </LinkComponent>
          {suffix}
        </Text>
      </Box>
    );
  }

  const milestoneMessage = getAuthenticatedMilestoneBannerMessage({
    cartTotalPrice,
    country,
    currency,
    isFirstOrder,
  });
  if (milestoneMessage) {
    return (
      <Box
        width="100%"
        backgroundColor="primary.75"
        padding="4px"
        textAlign="center"
        borderBottomLeftRadius="12px"
        borderBottomRightRadius="12px"
      >
        <Text
          textStyle="label.medium"
          fontWeight="medium"
          color="primary.500"
        >
          {parseMessageWithBold(milestoneMessage).map((part, i) => (
            <Text key={i} as="span" fontWeight={part.bold ? 'semibold' : 'medium'}>
              {part.text}
            </Text>
          ))}
        </Text>
      </Box>
    );
  }

  return null;
};
